import { useLayoutEffect, useState } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

type ScaleBy = 'fit' | 'width';

function calcFitScale(scaleBy: ScaleBy, parentwidth: number, parentheight: number, childwidth: number, childheight: number) {
  if (scaleBy === 'width') return parentwidth / childwidth;

  const parentRatio = parentwidth / parentheight;
  const childRatio = childwidth / childheight;
  return parentRatio < childRatio ? parentwidth / childwidth : parentheight / childheight;
}

export function useScaleToContainer<T extends HTMLElement>(padding = 0, scaleBy: ScaleBy = 'fit') {
  const [container, setContainer] = useState<T | null>(null);
  const [decorator, setDecorator] = useState<T | null>(null);
  const [content, setContent] = useState<T | null>(null);

  useLayoutEffect(() => {
    if (!container) return;
    if (!decorator) return;
    if (!content) return;

    const resizeObserver = createSafeResizeObserver(
      () => {
        // For square products such as napkins, there ends up being no padding, and feels tight.
        // So we are compensating for that here:
        const isSquare = content.clientWidth === content.clientHeight;
        const adjustment = isSquare ? 0.8 : 1;

        // prettier-ignore
        const zoom = adjustment * calcFitScale(
          scaleBy,
          Math.max(0, container.clientWidth - padding * 2),
          Math.max(0, container.clientHeight - padding * 2),
          content.clientWidth,
          content.clientHeight,
        );
        decorator.style.width = `${content.clientWidth * zoom}px`;
        decorator.style.height = `${content.clientHeight * zoom}px`;

        // Correct the centering
        content.style.position = 'relative';
        content.style.top = `${(content.clientHeight * zoom - content.clientHeight) / 2}px`;
        content.style.left = `${(content.clientWidth * zoom - content.clientWidth) / 2}px`;
        content.style.scale = `${zoom}`;
      },
      { skipRAF: true },
    );
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [container, decorator, content, padding, scaleBy]);

  // prettier-ignore
  return [
    (container: T) => setContainer(container),
    (decorator: T) => setDecorator(decorator),
    (content: T) => setContent(content)
  ] as const;
}
